// Tier Labels Context and Hook
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from '@/utils/api';

// Use same-origin API path on production custom domains
const API = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

// Default tier labels
const DEFAULT_TIER_LABELS = {
  founding_member: 'Founding Member',
  premium: 'Premium',
  free: 'Free',
  enterprise: 'Enterprise',
};

// Context for tier labels
const TierLabelsContext = createContext({
  labels: DEFAULT_TIER_LABELS,
  loading: true,
  refreshLabels: () => {},
});

// Provider component
export const TierLabelsProvider = ({ children }) => {
  const [labels, setLabels] = useState(DEFAULT_TIER_LABELS);
  const [loading, setLoading] = useState(true);

  const fetchLabels = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/admin/settings/tier-labels`);
      if (response.data.labels) {
        setLabels(response.data.labels);
      }
    } catch (error) {
      // Silent fail - use defaults
      console.log('Using default tier labels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  const refreshLabels = useCallback(() => {
    setLoading(true);
    fetchLabels();
  }, [fetchLabels]);

  const contextValue = useMemo(() => ({ 
    labels, 
    loading, 
    refreshLabels 
  }), [labels, loading, refreshLabels]);

  return (
    <TierLabelsContext.Provider value={contextValue}>
      {children}
    </TierLabelsContext.Provider>
  );
};

// Hook to use tier labels
export const useTierLabels = () => {
  const context = useContext(TierLabelsContext);
  if (!context) {
    return { labels: DEFAULT_TIER_LABELS, loading: false, refreshLabels: () => {} };
  }
  return context;
};

// Helper function to get label for a tier
export const getTierLabel = (labels, tierKey) => {
  return labels[tierKey] || tierKey.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export default TierLabelsContext;
